import { formatCurrency, cn } from "@/lib/utils";
import { parseDecimal, type Order } from "@/backend/types";
import { Spinner } from "@/components/spinner";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowRightLeftIcon,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import type { Fill } from "@/backend/types";
import { CollapsibleSection } from "./collapsible-section";
import { useFeesConfig } from "@/backend/hooks/use-fees-config";
import { getMarketPrecision } from "@/hooks/use-price-precision";
import { useI18n } from "@/features/i18n";

type HistoryTabProps = {
  fills: Fill[];
  orders: Order[];
  isLoading: boolean;
};

const copyOrderId = (orderId: string) => {
  navigator.clipboard.writeText(orderId);
  toast.success("Order ID copied to clipboard");
};

export const HistoryTab = ({
  fills,
  orders,
  isLoading,
}: HistoryTabProps) => {
  const { formatTradeDate } = useI18n();

  if (isLoading) {
    return (
      <div className="rounded-xl bg-card p-6 border border-border/50 shadow-sm dark:shadow-none">
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </div>
    );
  }

  const hasNoData = !fills.length && !orders.length;

  if (hasNoData) {
    return (
      <div className="rounded-xl bg-card p-6 border border-border/50 shadow-sm dark:shadow-none">
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <ArrowRightLeftIcon className="size-12 mb-3 opacity-50" />
          <p className="text-sm font-medium">No history in the selected period</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Trade History Section */}
      {fills.length > 0 && (
        <CollapsibleSection
          title="Trade History"
          subtitle={`${fills.length} trade${fills.length !== 1 ? "s" : ""}`}
          defaultExpanded={true}
        >
          <TradeHistoryContent fills={fills} formatTradeDate={formatTradeDate} />
        </CollapsibleSection>
      )}

      {/* Order Details Section */}
      {orders.length > 0 && (
        <CollapsibleSection
          title="Detailed Order History"
          subtitle={`Complete order records with IDs • ${orders.length} order${orders.length !== 1 ? "s" : ""}`}
          defaultExpanded={false}
        >
          <OrderHistoryContent orders={orders} formatTradeDate={formatTradeDate} />
        </CollapsibleSection>
      )}
    </div>
  );
};

// Trade History Content
const TradeHistoryContent = ({ fills, formatTradeDate }: { fills: Fill[]; formatTradeDate: (date: Date | string | number) => string }) => {
  return (
    <>
      {/* Table Header */}
      <div className="hidden md:grid md:grid-cols-7 gap-4 px-4 md:px-6 py-3 bg-muted/30 text-xs font-medium text-muted-foreground">
        <div>Market</div>
        <div>Side</div>
        <div className="text-right">Size</div>
        <div className="text-right">Price</div>
        <div className="text-right">Fee</div>
        <div className="text-right">Realized P&L</div>
        <div className="text-right">Date</div>
      </div>

      {/* Trades List */}
      <ScrollArea className="h-[400px]">
        <div className="divide-y divide-border/30">
          {fills.map((fill) => {
            const size = parseDecimal(fill.size);
            const price = parseDecimal(fill.price);
            const fee = parseDecimal(fill.fee);
            const realizedPnl = parseDecimal(fill.realized_pnl);
            const isLong = fill.side === "buy";
            const timestamp = new Date(fill.created_at);

            return (
              <div
                key={fill.id}
                className="px-4 md:px-6 py-4 hover:bg-muted/20 transition-colors"
              >
                {/* Mobile Layout */}
                <div className="md:hidden space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{fill.market}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatTradeDate(timestamp)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">
                        {size.toFixed(4)} @ ${formatCurrency(price, getMarketPrecision(fill.market).quotePrecision)}
                      </div>
                      <div
                        className={cn(
                          "text-sm",
                          realizedPnl >= 0 ? "text-bid-foreground" : "text-ask-foreground"
                        )}
                      >
                        {realizedPnl >= 0 ? "+" : ""}${formatCurrency(realizedPnl, 2)}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span
                      className={cn(
                        "px-2 py-0.5 rounded",
                        isLong ? "bg-bid text-bid-foreground" : "bg-ask text-ask-foreground"
                      )}
                    >
                      {isLong ? "Long" : "Short"}
                    </span>
                    <span>Fee: ${formatCurrency(fee, 4)}</span>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:grid md:grid-cols-7 gap-4 items-center">
                  {/* Market */}
                  <div>
                    <span className="font-medium text-sm">{fill.market}</span>
                  </div>

                  {/* Side */}
                  <div>
                    <span
                      className={cn(
                        "inline-flex items-center text-xs px-2 py-1 rounded capitalize",
                        isLong ? "bg-bid text-bid-foreground" : "bg-ask text-ask-foreground"
                      )}
                    >
                      {isLong ? "Long" : "Short"}
                    </span>
                  </div>

                  {/* Size */}
                  <div className="text-sm text-right font-medium">
                    {size.toFixed(4)}
                  </div>

                  {/* Price */}
                  <div className="text-sm text-right font-medium">
                    ${formatCurrency(price, getMarketPrecision(fill.market).quotePrecision)}
                  </div>

                  {/* Fee */}
                  <div className="text-sm text-right text-muted-foreground">
                    ${formatCurrency(fee, 4)}
                  </div>

                  {/* Realized P&L */}
                  <div
                    className={cn(
                      "text-sm text-right font-semibold",
                      realizedPnl >= 0 ? "text-bid-foreground" : "text-ask-foreground"
                    )}
                  >
                    {realizedPnl >= 0 ? "+" : ""}${formatCurrency(Math.abs(realizedPnl), 2)}
                  </div>

                  {/* Date */}
                  <div className="text-sm text-muted-foreground text-right">
                    {formatTradeDate(timestamp)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </>
  );
};

// Order History Content
const OrderHistoryContent = ({ orders, formatTradeDate }: { orders: Order[]; formatTradeDate: (date: Date | string | number) => string }) => {
  const { data: feesConfig } = useFeesConfig();

  return (
    <>
      {/* Table Header - Desktop */}
      <div
        className="hidden lg:grid lg:grid-cols-14 gap-3 px-4 md:px-6 py-3 bg-muted/30 text-xs font-medium text-muted-foreground"
        style={{
          gridTemplateColumns:
            "1.3fr 0.9fr 1.1fr 1fr 1fr 1.1fr 1.2fr 1fr 0.9fr 1fr 1.3fr 0.9fr 0.9fr 1.2fr",
        }}
      >
        <div>Time</div>
        <div>Type</div>
        <div>Symbol</div>
        <div>Direction</div>
        <div className="text-right">Size</div>
        <div className="text-right">Filled Size</div>
        <div className="text-right">Order Value</div>
        <div className="text-right">Price</div>
        <div className="text-right">Fee</div>
        <div className="text-center">Reduce Only</div>
        <div className="text-center">Trigger Conditions</div>
        <div className="text-center">TP/SL</div>
        <div>Status</div>
        <div className="text-center">Order ID</div>
      </div>

      {/* Orders List */}
      <ScrollArea className="h-[400px]">
        <div className="divide-y divide-border/30">
          {orders.map((order) => {
            const size = parseDecimal(order.size);
            const filledSize = parseDecimal(order.filled_size);
            const price = parseDecimal(order.price);
            const isBuy = order.side === "buy";
            const timestamp = new Date(order.created_at);
            const orderValue = filledSize * price;

            // Calculate fee based on order type using dynamic config from database
            const makerFeeBps = feesConfig?.maker_fee_bps
              ? parseFloat(feesConfig.maker_fee_bps)
              : 0;
            const takerFeeBps = feesConfig?.taker_fee_bps
              ? parseFloat(feesConfig.taker_fee_bps)
              : 0;
            const feeRate =
              order.order_type === "market"
                ? takerFeeBps / 10000
                : makerFeeBps / 10000;
            const fee = filledSize * price * feeRate;

            return (
              <div
                key={order.id}
                className="px-4 md:px-6 py-4 hover:bg-muted/20 transition-colors"
              >
                {/* Mobile Layout */}
                <div className="lg:hidden space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{order.market}</span>
                        <span
                          className={cn(
                            "text-xs px-1.5 py-0.5 rounded capitalize",
                            isBuy
                              ? "bg-bid text-bid-foreground"
                              : "bg-ask text-ask-foreground"
                          )}
                        >
                          {order.side}
                        </span>
                        <span className="text-xs capitalize text-muted-foreground">
                          {order.order_type}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatTradeDate(timestamp)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={cn(
                          "text-xs px-2 py-0.5 rounded capitalize inline-block mb-1",
                          order.status === "filled"
                            ? "bg-bid/20 text-bid-foreground"
                            : order.status === "cancelled" || order.status === "rejected"
                            ? "bg-muted text-muted-foreground"
                            : "bg-yellow-500/20 text-yellow-500"
                        )}
                      >
                        {order.status}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Size:</span>{" "}
                      <span className="font-medium">{size.toFixed(4)}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-muted-foreground">Filled:</span>{" "}
                      <span className="font-medium">{filledSize.toFixed(4)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Price:</span>{" "}
                      <span className="font-medium">
                        ${formatCurrency(price, getMarketPrecision(order.market).quotePrecision)}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-muted-foreground">Value:</span>{" "}
                      <span className="font-medium">${formatCurrency(orderValue, 2)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Fee:</span>{" "}
                      <span className="font-medium">${formatCurrency(fee, 4)}</span>
                    </div>
                    {order.reduce_only && (
                      <div className={order.reduce_only ? "col-span-1" : "col-span-2"}>
                        <span className="text-xs px-2 py-0.5 rounded bg-muted">
                          Reduce Only
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => copyOrderId(order.id)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-muted/30 hover:bg-muted/50 rounded transition-colors"
                  >
                    <span className="text-xs font-mono text-muted-foreground">
                      {order.id.slice(0, 24)}...
                    </span>
                    <Copy className="size-3 text-muted-foreground" />
                  </button>
                </div>

                {/* Desktop Layout */}
                <div
                  className="hidden lg:grid lg:grid-cols-14 gap-3 items-center text-sm"
                  style={{
                    gridTemplateColumns:
                      "1.3fr 0.9fr 1.1fr 1fr 1fr 1.1fr 1.2fr 1fr 0.9fr 1fr 1.3fr 0.9fr 0.9fr 1.2fr",
                  }}
                >
                  {/* Time */}
                  <div className="text-xs text-muted-foreground">
                    {formatTradeDate(timestamp)}
                  </div>

                  {/* Type */}
                  <div className="text-xs capitalize font-medium">
                    {order.order_type}
                  </div>

                  {/* Symbol (Market) */}
                  <div className="font-medium">{order.market}</div>

                  {/* Direction */}
                  <div
                    className={cn(
                      "font-medium capitalize",
                      isBuy ? "text-bid-foreground" : "text-ask-foreground"
                    )}
                  >
                    {order.side}
                  </div>

                  {/* Size */}
                  <div className="text-right font-medium">{size.toFixed(4)}</div>

                  {/* Filled Size */}
                  <div className="text-right font-medium">{filledSize.toFixed(4)}</div>

                  {/* Order Value */}
                  <div className="text-right font-medium">
                    {orderValue > 0 ? `$${formatCurrency(orderValue, 2)}` : "-"}
                  </div>

                  {/* Price */}
                  <div className="text-right font-medium">
                    {price > 0
                      ? `$${formatCurrency(price, getMarketPrecision(order.market).quotePrecision)}`
                      : "Market"}
                  </div>

                  {/* Fee */}
                  <div className="text-right text-muted-foreground">
                    ${formatCurrency(fee, 4)}
                  </div>

                  {/* Reduce Only */}
                  <div className="text-center text-muted-foreground text-xs">
                    {order.reduce_only ? "Yes" : "-"}
                  </div>

                  {/* Trigger Conditions */}
                  <div className="text-center text-muted-foreground text-xs">-</div>

                  {/* TP/SL */}
                  <div className="text-center text-muted-foreground text-xs">-</div>

                  {/* Status */}
                  <div>
                    <span
                      className={cn(
                        "inline-flex items-center text-xs px-2 py-0.5 rounded capitalize",
                        order.status === "filled"
                          ? "text-bid-foreground"
                          : order.status === "cancelled" || order.status === "rejected"
                          ? "text-muted-foreground"
                          : "text-yellow-500"
                      )}
                    >
                      {order.status}
                    </span>
                  </div>

                  {/* Order ID with Copy */}
                  <div className="flex items-center justify-center">
                    <button
                      onClick={() => copyOrderId(order.id)}
                      className="group flex items-center gap-1 px-2 py-1 hover:bg-muted/50 rounded transition-colors"
                      title="Click to copy full Order ID"
                    >
                      <span className="text-xs font-mono text-muted-foreground">
                        {order.id.slice(0, 8)}...
                      </span>
                      <Copy className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </>
  );
};


